import React, { useState, useEffect } from "react";
import { calendarAPI } from "../services/api";
import "./CalendarView.css";

const toDateKey = (value) => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split("T")[0];
  }

  const raw = String(value).trim();
  const ymd = raw.includes("T") ? raw.split("T")[0] : raw;
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split("T")[0];
  }

  return null;
};

const parseCalendarDate = (value) => {
  const key = toDateKey(value);
  if (!key) return null;
  const parsed = new Date(`${key}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const CalendarView = ({ courseId, onDateSelect }) => {
  const [events, setEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!courseId) return;

    const setInitialMonthFromCalendar = async () => {
      try {
        const response = await calendarAPI.getRange(courseId);
        const allEvents = (response.data || [])
          .map((event) => ({ ...event, _dateKey: toDateKey(event.date) }))
          .filter((event) => event._dateKey);

        if (allEvents.length === 0) {
          setCurrentMonth(new Date());
          return;
        }

        const sorted = [...allEvents].sort((a, b) =>
          a._dateKey.localeCompare(b._dateKey),
        );
        const today = new Date();
        const todayKey = today.toISOString().split("T")[0];

        const nextEvent =
          sorted.find((event) => event._dateKey >= todayKey) ||
          sorted[sorted.length - 1];

        const initialMonth = parseCalendarDate(nextEvent._dateKey) || new Date();
        setCurrentMonth(initialMonth);
      } catch (error) {
        console.error("Error setting initial calendar month:", error);
        setCurrentMonth(new Date());
      }
    };

    setInitialMonthFromCalendar();
  }, [courseId]);

  useEffect(() => {
    if (!courseId || !currentMonth) return;

    const loadCalendar = async () => {
      setLoading(true);
      try {
        const startDate = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth(),
          1,
        );
        const endDate = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth() + 1,
          0,
        );
        const response = await calendarAPI.getRange(
          courseId,
          startDate.toISOString().split("T")[0],
          endDate.toISOString().split("T")[0],
        );
        const normalizedEvents = (response.data || [])
          .map((event) => ({ ...event, _dateKey: toDateKey(event.date) }))
          .filter((event) => event._dateKey);
        setEvents(normalizedEvents);
      } catch (error) {
        console.error("Error loading calendar:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCalendar();
  }, [courseId, currentMonth]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    const base =
      currentMonth instanceof Date && !Number.isNaN(currentMonth.getTime())
        ? currentMonth
        : new Date();
    setCurrentMonth(
      new Date(base.getFullYear(), base.getMonth() - 1),
    );
  };

  const handleNextMonth = () => {
    const base =
      currentMonth instanceof Date && !Number.isNaN(currentMonth.getTime())
        ? currentMonth
        : new Date();
    setCurrentMonth(
      new Date(base.getFullYear(), base.getMonth() + 1),
    );
  };

  const getEventForDate = (day) => {
    const base =
      currentMonth instanceof Date && !Number.isNaN(currentMonth.getTime())
        ? currentMonth
        : new Date();
    const dateStr = new Date(
      base.getFullYear(),
      base.getMonth(),
      day,
    )
      .toISOString()
      .split("T")[0];
    return events.find((e) => e._dateKey === dateStr);
  };

  const safeCurrentMonth =
    currentMonth instanceof Date && !Number.isNaN(currentMonth.getTime())
      ? currentMonth
      : new Date();

  const monthName = safeCurrentMonth.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
  const daysInMonth = getDaysInMonth(safeCurrentMonth);
  const firstDay = getFirstDayOfMonth(safeCurrentMonth);
  const days = [];

  // Add empty cells for days before the month starts
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Add days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return (
    <div className="calendar-view">
      <div className="calendar-header" data-tour="calendar-controls">
        <h2>Scope & Sequence Calendar</h2>
        <button onClick={handlePrevMonth}>← Previous</button>
        <span className="month-year">{monthName}</span>
        <button onClick={handleNextMonth}>Next →</button>
      </div>

      {loading && <div className="loading">Loading calendar...</div>}

      <div className="calendar-grid-scroll" data-tour="calendar-grid">
        <div className="calendar-grid">
          <div className="weekday-header">Sun</div>
          <div className="weekday-header">Mon</div>
          <div className="weekday-header">Tue</div>
          <div className="weekday-header">Wed</div>
          <div className="weekday-header">Thu</div>
          <div className="weekday-header">Fri</div>
          <div className="weekday-header">Sat</div>

          {days.map((day, index) => {
            const event = day ? getEventForDate(day) : null;
            const hasLesson = Boolean(event?.lesson_id);
            const isCBA = event?.milestone?.includes("CBA");
            const isHoliday = /(holiday|break|no school|district closed|intersession)/i.test(
              event?.milestone || "",
            );
            const isBreak =
              Boolean(event) &&
              !isHoliday &&
              (event?.day_type === null || event?.lesson_id === null);

            return (
              <div
                key={index}
                className={`calendar-day ${!day ? "empty" : ""} ${hasLesson ? "has-lesson" : "no-lesson"} ${
                  isCBA ? "cba" : ""
                } ${isHoliday ? "holiday" : ""} ${isBreak ? "break" : ""}`}
                onClick={() => day && hasLesson && onDateSelect?.(event)}
              >
                {day && (
                  <>
                    <div className="day-number">{day}</div>
                    {event?.lesson_title && (
                      <button
                        type="button"
                        className="lesson-preview"
                        onClick={(clickEvent) => {
                          clickEvent.stopPropagation();
                          if (!hasLesson) return;
                          onDateSelect?.(event);
                        }}
                        title={event.lesson_title}
                        aria-label={`Open lesson: ${event.lesson_title}`}
                      >
                        {event.lesson_title.length > 18
                          ? `${event.lesson_title.substring(0, 18)}...`
                          : event.lesson_title}
                      </button>
                    )}
                    {event?.teks_code && (
                      <div
                        className="teks-badge"
                        title={
                          event.teks_description
                            ? `TEKS ${event.teks_code}: ${event.teks_description}`
                            : `TEKS ${event.teks_code}`
                        }
                      >
                        TEKS {event.teks_code}
                      </div>
                    )}
                    {event?.day_type && (
                      <div className="day-type">Day {event.day_type}</div>
                    )}
                    {event?.milestone && (
                      <div className="milestone-badge">{event.milestone}</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="calendar-legend" data-tour="calendar-legend">
        <div className="legend-item">
          <div className="legend-color"></div>
          <span>Regular Lesson Day</span>
        </div>
        <div className="legend-item cba">
          <div className="legend-color"></div>
          <span>CBA Assessment Window</span>
        </div>
        <div className="legend-item holiday-break">
          <div className="legend-color"></div>
          <span>Holiday/Break</span>
        </div>
        <div className="legend-item break-day">
          <div className="legend-color"></div>
          <span>No Lesson Scheduled</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
